import { useState, useEffect, useRef } from "react";
import {
  fetchMonActivite,
  fetchMesEtatsSortie,
  fetchMonTableauBord,
  type Period,
  type MonActiviteData,
  type MesEtatsSortieData,
  type MonTableauBordData,
} from "../../../api/professionnelStatistiques";
import { DonutChart } from "./stats/DonutChart";
import { HorizontalBar } from "./stats/HorizontalBar";
import { LineChart } from "./stats/LineChart";

// ─── Constantes ───────────────────────────────────────────────────────────────

const PERIOD_OPTIONS: Array<{ value: Period; label: string }> = [
  { value: "30j", label: "30 jours" },
  { value: "60j", label: "60 jours" },
  { value: "90j", label: "90 jours" },
  { value: "6m", label: "6 mois" },
  { value: "9m", label: "9 mois" },
  { value: "12m", label: "12 mois" },
  { value: "1a", label: "1 an" },
];

const TYPE_LABELS: Record<string, string> = {
  consultation_generale: "Générale",
  suivi: "Suivi",
  urgence: "Urgence",
  consultation_specialiste: "Spécialiste",
  consultation_urgence: "Urgence",
  consultation_telemedecine: "Télémédecine",
  consultation_chirurgie: "Chirurgie",
  consultation_maternite: "Maternité",
  consultation_pediatrie: "Pédiatrie",
  consultation_psychiatrie: "Psychiatrie",
};

const ETAT_ORDER = ["gueri", "en_traitement", "sorti_sans_avis_medical", "decede", "sans_etat"];
const ETAT_LABELS: Record<string, string> = {
  gueri: "Guéri",
  en_traitement: "En traitement",
  sorti_sans_avis_medical: "Sorti sans avis",
  decede: "Décédé",
  sans_etat: "Sans état",
};
const ETAT_COLORS = ["#22c55e", "#00553E", "#f97316", "#ef4444", "#94a3b8"];

const STATUT_LABELS: Record<string, string> = {
  ouverte: "Ouverte",
  fermee: "Fermée",
  annulee: "Annulée",
};
const STATUT_COLORS = ["#f97316", "#00553E", "#ef4444"];

const ASSURANCE_TYPE_LABELS: Record<string, string> = {
  AMO: "AMO",
  prive: "Privé",
  mutuelle: "Mutuelle",
  autre: "Autre",
};
const ASSURANCE_TYPE_COLORS = ["#00553E", "#3b82f6", "#a855f7", "#94a3b8"];

const ASSURANCE_STATUT_LABELS: Record<string, string> = {
  valide: "Valide",
  en_attente: "En attente",
  rejetee: "Rejetée",
  expiree: "Expirée",
};
const ASSURANCE_STATUT_COLORS = ["#22c55e", "#f97316", "#ef4444", "#94a3b8"];

// ─── Sous-composants ──────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  subtitle,
  color = "default",
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  color?: "default" | "green" | "orange" | "blue" | "red";
}) {
  const cls =
    color === "green" ? "text-green-700" :
    color === "orange" ? "text-orange-500" :
    color === "blue" ? "text-blue-600" :
    color === "red" ? "text-red-600" :
    "text-[#0B1F16]";
  return (
    <div className="rounded-[10px] border bg-white p-4" style={{ borderColor: "#E5E7EB" }}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${cls}`}>{value}</p>
      {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-4 text-base font-semibold text-[#0B1F16]">{children}</h2>;
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#00553E] border-t-transparent" />
    </div>
  );
}

function ProgressBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs text-gray-500">
        <span>{label}</span>
        <span className="font-semibold">{value.toFixed ? value.toFixed(2) : value}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function MonActivite() {
  const [period, setPeriod] = useState<Period>("30j");
  const [activite, setActivite] = useState<MonActiviteData | null>(null);
  const [etats, setEtats] = useState<MesEtatsSortieData | null>(null);
  const [tableau, setTableau] = useState<MonTableauBordData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingTableau, setLoadingTableau] = useState(true);
  const tableauLoaded = useRef(false);

  // Charge mon-tableau-bord une seule fois (indépendant de la période)
  useEffect(() => {
    if (tableauLoaded.current) return;
    tableauLoaded.current = true;
    setLoadingTableau(true);
    fetchMonTableauBord().then((res) => {
      if (res.ok) setTableau(res.data);
      setLoadingTableau(false);
    });
  }, []);

  // Recharge mon-activite + mes-etats-sortie à chaque changement de période
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchMonActivite(period), fetchMesEtatsSortie(period)]).then(([a, e]) => {
      if (a.ok) setActivite(a.data);
      if (e.ok) setEtats(e.data);
      setLoading(false);
    });
  }, [period]);

  const periodLabel = PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? period;

  // ─── Données graphiques ────────────────────────────────────────────────────

  const parTypeData = activite?.consultations?.parType
    ? Object.entries(activite.consultations.parType)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({ name: TYPE_LABELS[k] ?? k, value: v }))
    : [];

  const parStatutData = activite?.consultations?.parStatut
    ? Object.entries(activite.consultations.parStatut)
        .filter(([, v]) => v > 0)
        .map(([k, v], i) => ({ name: STATUT_LABELS[k] ?? k, value: v, itemStyle: { color: STATUT_COLORS[i] } }))
    : [];

  const parEtatData = etats?.parEtat
    ? ETAT_ORDER.filter((k) => (etats.parEtat as Record<string, number>)[k] > 0).map((k) => ({
        name: ETAT_LABELS[k],
        value: (etats.parEtat as Record<string, number>)[k],
      }))
    : [];

  const parEtatColors = etats?.parEtat
    ? ETAT_ORDER.filter((k) => (etats.parEtat as Record<string, number>)[k] > 0).map(
        (k) => ETAT_COLORS[ETAT_ORDER.indexOf(k)] ?? "#94a3b8"
      )
    : [];

  const assuranceTypeData = tableau?.assurances?.parType
    ? Object.entries(tableau.assurances.parType)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({ name: ASSURANCE_TYPE_LABELS[k] ?? k, value: v }))
    : [];

  const assuranceStatutData = tableau?.assurances?.parStatut
    ? Object.entries(tableau.assurances.parStatut)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({ name: ASSURANCE_STATUT_LABELS[k] ?? k, value: v }))
    : [];

  const latestSnapshot = tableau?.statistiquesParPeriode?.[0] ?? null;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div
        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-[12px] border bg-white px-5 py-4"
        style={{ borderColor: "#E5E7EB" }}
      >
        <div>
          <h1 className="text-lg font-bold text-[#0B1F16]">Mon activité</h1>
          <p className="text-sm text-gray-500">Statistiques de votre activité médicale</p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          className="self-start rounded-[8px] border px-3 py-2 text-sm text-[#0B1F16] focus:outline-none focus:ring-2 focus:ring-[#00553E]"
          style={{ borderColor: "#E5E7EB" }}
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {/* ─── Section 3.1 : Mon activité ─────────────────────────────────── */}

          {/* KPI cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label="Consultations (total)"
              value={activite?.consultations?.total ?? 0}
              subtitle="Historique complet"
            />
            <StatCard
              label={`Sur la période`}
              value={activite?.consultations?.surPeriode ?? 0}
              color="blue"
              subtitle={periodLabel}
            />
            <StatCard
              label="Ordonnances"
              value={activite?.ordonnances?.total ?? 0}
              color="orange"
              subtitle={periodLabel}
            />
            <StatCard
              label="Patients distincts"
              value={activite?.patientsDistincts ?? 0}
              color="green"
              subtitle="Historique complet"
            />
          </div>

          {/* Évolution mensuelle */}
          {(activite?.consultations?.evolutionMensuelle?.length ?? 0) > 0 && (
            <SectionCard>
              <SectionTitle>Évolution mensuelle des consultations</SectionTitle>
              <LineChart data={activite.consultations.evolutionMensuelle} height={260} />
            </SectionCard>
          )}

          {/* Types + Statuts côte à côte */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {parTypeData.length > 0 && (
              <SectionCard>
                <SectionTitle>Types de consultations — {periodLabel}</SectionTitle>
                <HorizontalBar data={parTypeData} />
              </SectionCard>
            )}
            {parStatutData.length > 0 && (
              <SectionCard>
                <SectionTitle>Répartition par statut</SectionTitle>
                <DonutChart
                  data={parStatutData.map(({ name, value }) => ({ name, value }))}
                  colors={STATUT_COLORS}
                  height={240}
                />
              </SectionCard>
            )}
          </div>

          {/* ─── Section 3.2 : États de sortie ──────────────────────────────── */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SectionCard>
              <SectionTitle>États de sortie — {periodLabel}</SectionTitle>
              <div className="mb-4 grid grid-cols-2 gap-3">
                <StatCard
                  label="Consultations clôturées"
                  value={etats?.totalFermees ?? 0}
                  subtitle={periodLabel}
                />
                <StatCard
                  label="Non clôturées"
                  value={etats?.consultationsNonCloturees ?? 0}
                  color={etats?.consultationsNonCloturees ? "orange" : "default"}
                  subtitle="Sans état défini"
                />
              </div>
              {parEtatData.length > 0 && (
                <DonutChart data={parEtatData} colors={parEtatColors} height={230} />
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle>Taux cliniques</SectionTitle>
              <div className="space-y-4 pt-2">
                <ProgressBar
                  label="Taux de guérison"
                  value={etats?.taux?.guerison ?? 0}
                  max={100}
                  color="#22c55e"
                />
                <ProgressBar
                  label="Sortie sans avis médical"
                  value={etats?.taux?.sortiSansAvis ?? 0}
                  max={100}
                  color="#f97316"
                />
                <ProgressBar
                  label="Taux de décès"
                  value={etats?.taux?.deces ?? 0}
                  max={100}
                  color="#ef4444"
                />
              </div>
              {etats && (
                <p className="mt-4 text-xs text-gray-400">
                  Sur {etats.totalFermees} consultations clôturées — {periodLabel}
                </p>
              )}
            </SectionCard>
          </div>
        </>
      )}

      {/* ─── Section 3.3 : Tableau de bord (indépendant de la période) ──────── */}

      {loadingTableau ? (
        <Spinner />
      ) : tableau ? (
        <>
          {/* Assurances */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {assuranceTypeData.length > 0 && (
              <SectionCard>
                <SectionTitle>Couverture assurance — par type</SectionTitle>
                <DonutChart data={assuranceTypeData} colors={ASSURANCE_TYPE_COLORS} height={240} />
              </SectionCard>
            )}
            {assuranceStatutData.length > 0 && (
              <SectionCard>
                <SectionTitle>Statuts des assurances</SectionTitle>
                <HorizontalBar
                  data={assuranceStatutData}
                  color="#00553E"
                  height={Math.max(160, assuranceStatutData.length * 44)}
                />
              </SectionCard>
            )}
          </div>

          {/* Snapshot le plus récent */}
          {latestSnapshot && (
            <SectionCard>
              <SectionTitle>
                Mon tableau de bord — période {latestSnapshot.periode}
                <span className="ml-2 text-xs font-normal text-gray-400">
                  (calculé le {formatDate(latestSnapshot.dateGeneration)})
                </span>
              </SectionTitle>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <StatCard label="Mes patients" value={latestSnapshot.plateforme?.totalInscrits ?? 0} />
                <StatCard label="Nouveaux patients" value={latestSnapshot.plateforme?.nouveauxInscrits ?? 0} color="green" />
                <StatCard label="Consultations réalisées" value={latestSnapshot.plateforme?.consultationsRealisees ?? 0} />
                <StatCard label="Analyses prescrites" value={latestSnapshot.plateforme?.analysesPrescrites ?? 0} />
                <StatCard label="Ordonnances délivrées" value={latestSnapshot.plateforme?.ordonnancesDelivrees ?? 0} color="orange" />
                <StatCard label="Parfaite santé" value={latestSnapshot.santeGlobale?.parfaiteSante ?? 0} color="green" />
                <StatCard label="En traitement" value={latestSnapshot.santeGlobale?.enTraitement ?? 0} color="orange" />
              </div>
            </SectionCard>
          )}

          {/* Total assurances */}
          {(tableau?.assurances?.total ?? 0) > 0 && (
            <div className="rounded-[10px] border bg-white px-5 py-3 text-sm text-gray-500" style={{ borderColor: "#E5E7EB" }}>
              Total assurances enregistrées :{" "}
              <span className="font-semibold text-[#0B1F16]">{tableau!.assurances!.total}</span>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
